import { useEffect, useState } from 'react';
import { Clock, XCircle, AlertCircle, Loader2, Edit } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import ProfileCompletionForm from './ProfileCompletionForm';
import { useAuthContext } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios.config';
import { toast } from 'sonner';
import { AtootbandanLogo } from './AtootbandanLogo';

interface ProfileCompletionWrapperProps {
  onComplete: () => void;
}

export default function ProfileCompletionWrapper({ onComplete }: ProfileCompletionWrapperProps) {
  const { user, loadUser } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profileStatus, setProfileStatus] = useState<'none' | 'pending' | 'rejected' | 'approved'>('none');
  const [rejectionReason, setRejectionReason] = useState('');
  const [hasProfile, setHasProfile] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  // Wait for user to load if not available yet
  useEffect(() => {
    const waitForUser = async () => {
      if (!user) {
        const token = localStorage.getItem('token');
        if (token) {
          try {
            // Small delay to ensure token is fully set in axios
            await new Promise(resolve => setTimeout(resolve, 200));
            await loadUser();
          } catch (error) {
            // Token exists but load failed - redirect to login
            navigate('/login', { replace: true });
          }
        } else {
          // No token - redirect to login
          navigate('/login', { replace: true });
        }
      }
    };
    
    waitForUser();
  }, [user, loadUser, navigate]);

  // Immediately check user status and redirect if needed (synchronously)
  useEffect(() => {
    if (!user) return;
    
    const verificationStatus = user.adminVerification?.status;
    
    // If approved, redirect to app immediately
    if (verificationStatus === 'approved' && user.profileCompleted) {
      navigate('/app', { replace: true });
      return;
    }
    
    // If pending, redirect to admin-pending screen immediately
    if (verificationStatus === 'pending' && user.profileCompleted) {
      navigate('/admin-pending', { replace: true });
      return;
    }
    
    // If rejected, show the form to allow updates
    if (verificationStatus === 'rejected') {
      setProfileStatus('rejected');
      setRejectionReason(user.adminVerification?.rejectionReason || 'Your profile did not meet our verification standards.');
      setShowEditForm(true);
      setLoading(false);
      return;
    }
    
    // If no profile yet or not completed, show the form
    setProfileStatus('none');
    setLoading(false);
  }, [user, navigate]);

  const checkProfileStatus = async () => {
    try {
      setLoading(true);
      
      // Check if profile exists
      try {
        const response = await axios.get('/profiles/me');
        if (response.data?.success && response.data?.data) {
          const profile = response.data.data;
          
          // If profile exists and has some data, consider it as created
          if (profile._id && profile.userId) {
            setHasProfile(true);
            
            // Check if user has profileCompleted flag
            if (user?.profileCompleted) {
              // Profile is completed but not verified - show pending
              navigate('/admin-pending', { replace: true });
            } else {
              // Profile exists but not completed - allow editing
              setProfileStatus('none');
            }
          } else {
            setProfileStatus('none');
            setHasProfile(false);
          }
        } else {
          setProfileStatus('none');
          setHasProfile(false);
        }
      } catch (error: any) {
        // If 404, profile doesn't exist
        if (error.response?.status === 404) {
          setProfileStatus('none');
          setHasProfile(false);
        }
      }
    } catch (error) {
      setProfileStatus('none');
      setHasProfile(false);
    } finally {
      setLoading(false);
    }
  };

  const handleFormComplete = async () => {
    await loadUser();
    onComplete();
  };

  const handleRetry = () => {
    setShowEditForm(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="size-12 animate-spin mx-auto mb-4 text-rose-600" />
          <p className="text-gray-600">Loading profile status...</p>
        </div>
      </div>
    );
  }

  // Profile is rejected - show rejection message and edit form
  if (profileStatus === 'rejected') {
    return <ProfileCompletionForm onComplete={handleFormComplete} isResubmission={true} rejectionReason={rejectionReason} />;
  }

  // No profile or incomplete - show form
  return <ProfileCompletionForm onComplete={handleFormComplete} isResubmission={false} />;
}